import { FC } from "react"

import Accordion from "../../../../accordion"
import SectionCell from "../../../../section/cell"
import LoanScenario from "./accordion-sections/loan-scenario"
import PropertyLocation from "./accordion-sections/property-location"
import Misc from "./accordion-sections/misc"
import Message from "./accordion-sections/message"
import If from "../../../../if"
import Tooltip from "../../../../tool-tip"
import { formatPhoneNumber, formatDateTimeFromISO } from "../../../../../utils"
import { LeadDetailsAccordion } from "../../../../../constants"
import { Lead } from "../../../../../../types"
import { useInMobileView } from "../../../../../hooks/use-window-size"

const DASH_LINK_MAX = 34

interface Props {
  data: Lead
  dashLink?: string
  defaultOpenAccordions?: LeadDetailsAccordion[]
}

const LeadSummary: FC<Props> = ({ data, dashLink, defaultOpenAccordions }) => {
  const inMobileView = useInMobileView()
  const cols = inMobileView ? 2 : 3

  return (
    <div className="max-w-3xl">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <SectionCell
          label="First name"
          value={data.first_name}
          className="!pt-0"
        />
        <SectionCell
          label="Last name"
          value={data.last_name}
          className="!pt-0"
        />
        <SectionCell
          label="Email address"
          value={data.email}
          className="!pt-0"
          truncateValue
        />
        <SectionCell
          label="Phone number"
          value={formatPhoneNumber(data.phone ?? "")}
          className="!pt-0"
        />
        <SectionCell
          label="Created by"
          value={data.owner}
          className="!pt-0"
          truncateValue
        />
        <SectionCell
          label="Created on"
          value={formatDateTimeFromISO(data.date_time)}
          className="!pt-0"
        />
        <If
          condition={!!dashLink}
          then={
            <div className="pt-0">
              <div className="pb-1 text-neutral-400">Dashboard link</div>
              <div className="text-neutral-900 h-7">
                {dashLink && dashLink.length > DASH_LINK_MAX ? (
                  <Tooltip
                    toolTipNode={
                      <a href={dashLink} target="_blank" rel="noreferrer">
                        {dashLink.slice(0, DASH_LINK_MAX)}…
                      </a>
                    }
                    text={dashLink}
                    placement="top"
                  />
                ) : (
                  <a href={dashLink} target="_blank" rel="noreferrer">
                    {dashLink}
                  </a>
                )}
              </div>
            </div>
          }
        />
      </div>

      <div className="grid gap-4 mt-6">
        <div>
          <Accordion
            title={LeadDetailsAccordion.PropertyLocation}
            openByDefault={
              defaultOpenAccordions?.includes(
                LeadDetailsAccordion.PropertyLocation
              ) ?? true
            }
          >
            <PropertyLocation cols={cols} data={data} />
          </Accordion>
        </div>
        <div>
          <Accordion
            title={LeadDetailsAccordion.LoanScenario}
            openByDefault={
              defaultOpenAccordions?.includes(
                LeadDetailsAccordion.LoanScenario
              ) ?? true
            }
          >
            <LoanScenario cols={cols} data={data} />
          </Accordion>
        </div>
        <div>
          <Accordion
            title={LeadDetailsAccordion.Misc}
            openByDefault={
              defaultOpenAccordions?.includes(LeadDetailsAccordion.Misc) ?? true
            }
          >
            <Misc cols={cols} data={data} />
          </Accordion>
        </div>
        {data?.message !== "" && (
          <div>
            <Accordion
              title={LeadDetailsAccordion.Message}
              openByDefault={
                defaultOpenAccordions?.includes(LeadDetailsAccordion.Message) ??
                true
              }
            >
              <Message data={data} />
            </Accordion>
          </div>
        )}
      </div>
    </div>
  )
}

export default LeadSummary
